import { LOG_SAGA_TIME, SLOW_DOWN_SAGA } from 'config/app';
import Glue from 'Glue';
import { Store } from 'redux';
import { all, call, delay, put } from 'redux-saga/effects';
import { loadingActions, loadingSelectors } from 'store/glue/loading';
import { toastActions } from 'store/glue/toast';
import { toSafeToasts } from 'store/toastSchema';
import Types from 'Types';

const SLOW_REQUEST = 500;

/**
 * call this function with an effect to achieve desired result.
 * most case, it will be called with a simple call
 */

function* sagaWrapper(args: {
  saga: () => Generator<any, /*FunctionalEvent |*/ void>;

  type: Glue.LoadingType;
  logTime?: boolean;
}) {
  const { saga, type } = args;
  const { logTime = false } = args;

  const startTime = new Date().getTime();
  // Tagged in the log line below so a cold-tier hunt can tell apart e.g.
  // a slow IdP (FAILED: glue/user/START_VERIFY) from a slow data API.
  let failed = false;

  try {
    yield put(loadingActions.updateLoadingStatus({ type, status: 'loading' }));
    yield call(saga);
    // Slow down
    const excecutionTime = new Date().getTime() - startTime;
    if (SLOW_DOWN_SAGA && excecutionTime < SLOW_REQUEST)
      yield delay(SLOW_REQUEST - excecutionTime);

    yield put(
      loadingActions.updateLoadingStatus({ type, status: 'succeeded' }),
    );
  } catch (error) {
    failed = true;
    const toasts = toSafeToasts(error);
    yield all(toasts.map((toast) => put(toastActions.addToast(toast))));

    yield put(
      loadingActions.updateLoadingStatus({
        type,
        status: 'failed',
        error,
      }),
    );
  } finally {
    const endTime = new Date().getTime();
    const duration = endTime - startTime;
    yield put(loadingActions.updateLoadingStatus({ type, status: 'idle' }));
    if (logTime || LOG_SAGA_TIME) {
      const tag = failed ? 'FAILED' : 'EVENT';
      // eslint-disable-next-line no-console
      console.log(`${tag}: ${type} took ${duration}ms`);
    }
  }
}

/**
 * Returns a Promise that resolves when the given loading type clears.
 * Use in route loaders to bridge a Redux saga to TanStack Router's pending
 * state — dispatch an action, then await this to block navigation until
 * the saga finishes.
 */
function waitForLoading(
  store: Store<Types.RootState>,
  type: Glue.LoadingType,
): Promise<void> {
  return new Promise<void>((resolve) => {
    // Already clear — init completed before this ran (e.g. a loader re-run).
    if (!loadingSelectors.isLoading(store.getState(), { types: [type] })) {
      resolve();
      return;
    }

    // Poll on every Redux state change until the loading slot clears.
    // Fires more often than needed but the check is cheap. Self-destructs
    // on first match so no dangling listener remains after init.
    const unsubscribe = store.subscribe(() => {
      if (!loadingSelectors.isLoading(store.getState(), { types: [type] })) {
        unsubscribe();
        resolve();
      }
    });
  });
}

export { sagaWrapper, waitForLoading };
